import { auth } from "@baton/api";
import { lastRouteKey, lastWorkspaceKey, modeRouteKey, recentItemsKey, type Runtime } from "@baton/core";
import { useToastStore } from "@baton/ui";
import { endWorkspaceSessions } from "../../shell/realtime/registry.ts";
import { signInEntry } from "../../teams/tab.ts";

/**
 * FR-015-013, FR-015-016, `contracts/client-api.md` § Session lifecycle: sign-out clears the
 * query cache, the selection, the drafts, the per-workspace last route and each mode's last
 * route, and navigates to
 * `/sign-in` (the tab's root inside Microsoft Teams, `teams/tab.ts`); sign-out-everywhere additionally drops the offline queue, because those
 * writes belong to a session that no longer exists. The palette's recent items and the
 * pending toasts go with the session too, every workspace event stream is closed with its resume cursor forgotten, so a shared machine keeps nothing readable.
 */
export async function signOut(runtime: Runtime, options: { readonly everywhere?: boolean } = {}): Promise<void> {
  const everywhere = options.everywhere === true;
  let confirmed = false;
  try {
    if (everywhere) await auth.signOutAll(runtime.client, { retry: false });
    else await auth.signOut(runtime.client, { retry: false });
    confirmed = true;
  } finally {
    clearSessionState(runtime, everywhere, confirmed ? "sign-out" : "session-ended");
    window.location.assign(signInEntry());
  }
}

/**
 * What sign-out wipes locally; the dismissed re-authentication dialog runs the same. `closing` says
 * whether the server confirmed the sign-out (2026-10-14): only a confirmed `sign-out` closes the event
 * stream in the browser's other tabs, because after a failed one, or the re-authentication dialog's Sign
 * out, which ends no session on the server, another tab may hold a session that is still valid.
 */
export function clearSessionState(runtime: Runtime, everywhere: boolean, closing: "sign-out" | "session-ended"): void {
  endWorkspaceSessions(runtime, closing);
  runtime.signOut({ everywhere });
  for (const scope of runtime.storage.scopesOf(lastRouteKey)) runtime.storage.remove(lastRouteKey, scope);
  for (const scope of runtime.storage.scopesOf(modeRouteKey)) runtime.storage.remove(modeRouteKey, scope);
  runtime.storage.remove(lastWorkspaceKey);
  for (const scope of runtime.storage.scopesOf(recentItemsKey)) runtime.storage.remove(recentItemsKey, scope);
  useToastStore.getState().clear();
}
